// Contracts — pothos payouts (manual request model, migration 0064).
// Worker side: apps/api/src/pothos/payouts.ts. Web side: getPayoutState /
// submitPayoutRequest in apps/web/src/pothos/api.ts.
import { z } from 'zod';

export const PayoutMethodSchema = z.enum(['wise', 'payoneer']);
export type PayoutMethod = z.infer<typeof PayoutMethodSchema>;

export const PayoutStatusSchema = z.enum(['requested', 'processing', 'paid', 'cancelled']);
export type PayoutStatus = z.infer<typeof PayoutStatusSchema>;

// toActive() projection of a payout_requests row. All columns are
// NOT NULL with '' defaults (0064): timestamps are ISO strings, with ''
// meaning "not reached yet" (processing_at / paid_at), never null.
export const PayoutRequestSchema = z.object({
  id: z.string(),
  amount_usd: z.number(),
  amount_native: z.number(),
  native_currency: z.string(),
  method: PayoutMethodSchema,
  email: z.string(),
  status: PayoutStatusSchema,
  manager_note: z.string(),
  requested_at: z.string(),
  processing_at: z.string(),
  paid_at: z.string(),
});
export type PayoutRequest = z.infer<typeof PayoutRequestSchema>;

// GET /api/payouts/state
export const PothosPayoutsStateResSchema = z.object({
  feature_enabled: z.boolean(),
  email: z.string(),
  balance_usd: z.number(),
  balance_native_amount: z.number(),
  // null until the user has a payout_currency set.
  balance_native_currency: z.string().nullable(),
  min_wise_usd: z.number(),
  min_payoneer_usd: z.number(),
  min_wise_native: z.number(),
  min_payoneer_native: z.number(),
  payoneer_unlocked: z.boolean(),
  active: PayoutRequestSchema.nullable(),
  history: z.array(PayoutRequestSchema),
});
export type PothosPayoutsStateRes = z.infer<typeof PothosPayoutsStateResSchema>;

// POST /api/payouts/request — amount is server-authoritative (full
// available balance); email falls back to the account email AFTER parse
// (domain logic in the handler, including the 'valid email required'
// check the web matches on).
export const PothosPayoutsRequestReqSchema = z.object({
  method: PayoutMethodSchema,
  email: z.string().trim().optional(),
});
export type PothosPayoutsRequestReq = z.infer<typeof PothosPayoutsRequestReqSchema>;

export const PothosPayoutsRequestResSchema = z.object({
  ok: z.literal(true),
  active: PayoutRequestSchema,
});
export type PothosPayoutsRequestRes = z.infer<typeof PothosPayoutsRequestResSchema>;

// Domain error of payouts/request — shape is load-bearing: the web
// matches the 'below_minimum' code (CabinetRequestPayout). Never change
// without a coordinated client release.
export const PothosPayoutsBelowMinimumErrSchema = z.object({
  error: z.literal('below_minimum'),
  method: PayoutMethodSchema,
  min_native: z.number(),
  balance_native: z.number(),
  native_currency: z.string(),
});
export type PothosPayoutsBelowMinimumErr = z.infer<typeof PothosPayoutsBelowMinimumErrSchema>;

// MANAGER: POST /api/payouts/admin/advance
export const PothosPayoutsAdminAdvanceReqSchema = z.object({
  id: z.string().trim().min(1),
  status: z.enum(['processing', 'paid', 'cancelled']),
  manager_note: z.string().trim().optional(),
});
export type PothosPayoutsAdminAdvanceReq = z.infer<typeof PothosPayoutsAdminAdvanceReqSchema>;

export const PothosPayoutsAdminAdvanceResSchema = z.object({
  ok: z.literal(true),
  request: PayoutRequestSchema,
});
export type PothosPayoutsAdminAdvanceRes = z.infer<typeof PothosPayoutsAdminAdvanceResSchema>;

// MANAGER: GET /api/payouts/admin/list — raw payout_requests rows
// (pr.*, not the toActive projection) + account_email from a LEFT JOIN
// on users (null when the user row is gone).
export const PayoutRequestAdminRowSchema = PayoutRequestSchema.extend({
  user_id: z.string(),
  referrer: z.string(),
  account_email: z.string().nullable(),
});
export type PayoutRequestAdminRow = z.infer<typeof PayoutRequestAdminRowSchema>;

export const PothosPayoutsAdminListResSchema = z.object({
  requests: z.array(PayoutRequestAdminRowSchema),
});
export type PothosPayoutsAdminListRes = z.infer<typeof PothosPayoutsAdminListResSchema>;
